import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export const trackPageView = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      path: z.string().min(1).max(500),
      insightId: z.string().uuid().optional(),
    }),
  )
  .handler(async ({ data }) => {
    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const { and, eq, sql } = await import("drizzle-orm");
    const { getClientIp, hashVisitor, isBotUserAgent, maskIp } = await import("../analytics/ip.server");
    const { lookupGeo } = await import("../analytics/geo.server");
    const { cacheSetNx, rateLimitCheck } = await import("../cache/redis.server");
    const { getDb, schema } = await import("../db/client.server");

    const path = data.path.split("?")[0] ?? data.path;
    if (path.startsWith("/admin")) return { ok: true, skipped: true };

    const userAgent = getRequestHeader("user-agent");
    if (isBotUserAgent(userAgent)) return { ok: true, skipped: true };

    const ip = getClientIp();
    const visitorHash = hashVisitor(ip);
    const allowed = await rateLimitCheck(`track:${visitorHash}`, 60, 60);
    if (!allowed) return { ok: false, rateLimited: true };

    const geo = lookupGeo(ip);

    let insightId = data.insightId ?? null;
    if (!insightId) {
      const match = /^\/insights\/([^/?#]+)/.exec(path);
      if (match) {
        const slug = decodeURIComponent(match[1]);
        const db = getDb();
        const [row] = await db
          .select({ id: schema.insights.id })
          .from(schema.insights)
          .where(and(eq(schema.insights.slug, slug), eq(schema.insights.status, "published")))
          .limit(1);
        insightId = row?.id ?? null;
      }
    }

    const date = new Date().toISOString().slice(0, 10);
    const isNewUnique = await cacheSetNx(`uv:${date}:${visitorHash}`, 48 * 3600);

    const db = getDb();
    await db.insert(schema.siteVisits).values({
      path,
      insightId,
      visitorHash,
      ipMasked: maskIp(ip),
      countryCode: geo.countryCode,
      countryName: geo.countryName,
      region: geo.region,
      city: geo.city,
      latitude: geo.latitude,
      longitude: geo.longitude,
      userAgent: userAgent?.slice(0, 512) ?? null,
      referer: getRequestHeader("referer")?.slice(0, 512) ?? null,
    });

    await db
      .insert(schema.analyticsDailyStats)
      .values({
        date,
        uniqueVisitors: isNewUnique ? 1 : 0,
        totalPageViews: 1,
      })
      .onConflictDoUpdate({
        target: schema.analyticsDailyStats.date,
        set: {
          uniqueVisitors: sql`${schema.analyticsDailyStats.uniqueVisitors} + ${isNewUnique ? 1 : 0}`,
          totalPageViews: sql`${schema.analyticsDailyStats.totalPageViews} + 1`,
        },
      });

    if (insightId) {
      await db
        .insert(schema.insightImpressions)
        .values({
          insightId,
          impressions: 1,
        })
        .onConflictDoUpdate({
          target: schema.insightImpressions.insightId,
          set: {
            impressions: sql`${schema.insightImpressions.impressions} + 1`,
            updatedAt: new Date(),
          },
        });
    }

    return { ok: true };
  });
